/**
 * The client switcher's rules (design 10.2), pure so they can be tested:
 *   - the current client is the URL and nothing else;
 *   - choosing a client goes to the same view in that client when the view is a client view, else to its home;
 *   - "Recent" is a convenience kept in localStorage (wrapped), never used to pick a client.
 */

const RECENT_KEY = 'olivia.recentClients'

/** The client code in a path, or null outside a client. */
export function clientOf(pathname: string): string | null {
  const m = /^\/c\/([a-z0-9][a-z0-9-]{1,38}[a-z0-9])(?:\/|$)/.exec(pathname)
  return m ? (m[1] ?? null) : null
}

/**
 * The same view in another client, or that client's home. A record belongs to one client, so a record's page
 * becomes the list it sits in (CY-2026-041 of one client is not the other's): /c/a/cycle/CY-2026-041 becomes
 * /c/b/cycle, /c/a/cycle/series/SER-003 becomes /c/b/cycle. Onboarding is the client's own and goes home.
 */
export function sameViewIn(pathname: string, code: string): string {
  const m = /^\/c\/[a-z0-9-]+(\/.*)?$/.exec(pathname)
  if (!m) return `/c/${code}`
  const parts = (m[1] ?? '').split('/').filter(Boolean)
  const view: string[] = []
  for (const p of parts) {
    if (/^[A-Z]{2,5}-/.test(p) || p === 'series' || p === 'templates' || p === 'new') break
    view.push(p)
  }
  if (view[0] === 'onboarding') return `/c/${code}`
  return view.length ? `/c/${code}/${view.join('/')}` : `/c/${code}`
}

export function readRecent(): string[] {
  try {
    const raw = window.localStorage.getItem(RECENT_KEY)
    const list = raw ? (JSON.parse(raw) as unknown) : []
    return Array.isArray(list) ? list.filter((x): x is string => typeof x === 'string').slice(0, 5) : []
  } catch {
    return []
  }
}

export function rememberRecent(code: string): void {
  try {
    const next = [code, ...readRecent().filter((c) => c !== code)].slice(0, 5)
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(next))
  } catch {
    // Storage blocked: the switcher simply shows no recent list.
  }
}
